/**
 * Owner: Masa Bayu
 * File: marketplace-projection.ts
 * Date:Time: 2026-10-22 23:52
 * Description: Home marketplace projection — katalog hub ke kad buku, penuras,
 *   isihan dan muka surat yang deterministik. §466 S1: tiada lagi nilai rekaan
 *   (rating/jumlah ulasan/lencana dahulunya dijana daripada hash id). Identiti
 *   (sort/kategori) neutral locale; semua label paparan dalam marketplace-labels.
 */

import { listCatalog } from '../../publication/catalog-port';
import { getHubPrice } from '../../publication/hub-pricing';
import {
  isQxMainCategoryId,
  isQxSubcategoryId,
  qxCategoryName,
  qxParentOfSubcategory,
  QX_MAIN_CATEGORY_IDS,
  type QxLanguage,
  type QxMainCategoryId,
} from '@qx/qxread-core';

/** §405 — kategori utama ialah taksonomi QX penuh (35 id stabil dari qxread-core). */
export type MarketplaceCategoryId = QxMainCategoryId;

export type MarketplacePriceBand = 'free' | 'b1_10' | 'b11_30' | 'b31_50' | 'b51_plus';

/** §466 S1 — isihan hanya pada medan yang wujud di pelayan. */
export type MarketplaceSortId = 'newest' | 'price_low' | 'price_high' | 'title_a_z';

export interface MarketplaceBook {
  readonly publicationId: string;
  readonly title: string;
  /** null = hub belum menghantar penulis; UI papar kosong, bukan nama palsu. */
  readonly author: string | null;
  readonly description: string | null;
  /** null = terbitan belum ditandakan dengan kategori (§405) di manifest. */
  readonly categoryId: MarketplaceCategoryId | null;
  readonly subcategoryId: string | null;
  readonly secondaryCategoryIds: readonly MarketplaceCategoryId[];
  readonly priceMinorUnits: number | null;
  readonly currency: string | null;
  readonly publishedAt: string | null;
  readonly languageRef: string | null;
  readonly sampleMaxPage: number | null;
  /** Nada hiasan cover ikut kedudukan rak — bukan data, tiada klaim ke atas buku. */
  readonly coverTone: number;
}

export interface MarketplaceFilters {
  readonly category: MarketplaceCategoryId | 'all';
  readonly subcategory: string | 'all';
  readonly price: MarketplacePriceBand | 'all';
  readonly query: string;
}

export const MARKETPLACE_CATEGORY_ORDER: readonly MarketplaceCategoryId[] = QX_MAIN_CATEGORY_IDS;

/** Peta label kategori penuh taksonomi QX ikut bahasa. */
export function categoryLabelsFor(
  language: QxLanguage,
): Readonly<Record<MarketplaceCategoryId, string>> {
  return Object.freeze(
    Object.fromEntries(
      QX_MAIN_CATEGORY_IDS.map((id) => [id, qxCategoryName(id, language)]),
    ) as Record<MarketplaceCategoryId, string>,
  );
}

export const MARKETPLACE_PRICE_BAND_ORDER: readonly MarketplacePriceBand[] = Object.freeze([
  'free',
  'b1_10',
  'b11_30',
  'b31_50',
  'b51_plus',
]);

export const MARKETPLACE_SORT_ORDER: readonly MarketplaceSortId[] = Object.freeze([
  'newest',
  'price_low',
  'price_high',
  'title_a_z',
]);

export const MARKETPLACE_PAGE_SIZE = 12;

export const MARKETPLACE_FILTERS_EMPTY: MarketplaceFilters = Object.freeze({
  category: 'all',
  subcategory: 'all',
  price: 'all',
  query: '',
});

/** Harga belum diketahui (tiada descriptor hub) → tiada baris penuras harga. */
function priceBandFor(priceMinorUnits: number | null): MarketplacePriceBand | null {
  if (priceMinorUnits === null) return null;
  if (priceMinorUnits <= 0) return 'free';
  if (priceMinorUnits <= 1000) return 'b1_10';
  if (priceMinorUnits <= 3000) return 'b11_30';
  if (priceMinorUnits <= 5000) return 'b31_50';
  return 'b51_plus';
}

type MetadataValue = string | readonly string[] | null | undefined;

function metadataText(value: MetadataValue): string | null {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const list: readonly unknown[] = value;
    const first = list[0];
    return typeof first === 'string' ? first : null;
  }
  return null;
}

function metadataStringList(value: MetadataValue): readonly string[] {
  if (Array.isArray(value)) {
    const list: readonly unknown[] = value;
    return list.filter((item): item is string => typeof item === 'string');
  }
  return [];
}

function firstAuthor(entry: {
  readonly metadata: Readonly<Record<string, string | readonly string[] | null>>;
}): string | null {
  return metadataStringList(entry.metadata.authors)[0] ?? null;
}

/** Taksonomi dibaca daripada tag §405 sebenar (main · sub · sekunder) dalam manifest. */
function taxonomyOf(tags: readonly string[]): {
  readonly categoryId: MarketplaceCategoryId | null;
  readonly subcategoryId: string | null;
  readonly secondaryCategoryIds: readonly MarketplaceCategoryId[];
} {
  const sub = tags.find((tag) => isQxSubcategoryId(tag)) ?? null;
  const parentOfSub = sub === null ? null : qxParentOfSubcategory(sub);
  const main = tags.find((tag) => isQxMainCategoryId(tag));
  const categoryId = (parentOfSub ?? main ?? null) as MarketplaceCategoryId | null;
  const secondary = Object.freeze(
    tags.filter(
      (tag): tag is MarketplaceCategoryId => isQxMainCategoryId(tag) && tag !== categoryId,
    ),
  );
  return { categoryId, subcategoryId: sub, secondaryCategoryIds: secondary };
}

export function listMarketplaceBooks(): readonly MarketplaceBook[] {
  return listCatalog().map((entry, index) => {
    const tags = metadataStringList(entry.metadata.tags);
    const taxonomy = taxonomyOf(tags);
    const publishedAt = metadataText(entry.metadata.publishedAt);
    // Harga SSOT = descriptor hub; tiada tawaran demo setempat.
    const hubPrice = getHubPrice(entry.publicationId);
    return Object.freeze({
      publicationId: entry.publicationId,
      title: entry.title,
      author: firstAuthor(entry),
      description: metadataText(entry.metadata.description),
      categoryId: taxonomy.categoryId,
      subcategoryId: taxonomy.subcategoryId,
      secondaryCategoryIds: taxonomy.secondaryCategoryIds,
      priceMinorUnits: hubPrice?.priceMinorUnits ?? null,
      currency: hubPrice?.currency ?? null,
      publishedAt,
      languageRef: metadataText(entry.metadata.language),
      sampleMaxPage: hubPrice?.sampleMaxPage ?? null,
      coverTone: index % 8,
    });
  });
}

function matchesBand(priceMinorUnits: number | null, band: MarketplacePriceBand): boolean {
  return priceBandFor(priceMinorUnits) === band;
}

export function matchesFilters(book: MarketplaceBook, filters: MarketplaceFilters): boolean {
  if (filters.category !== 'all') {
    const inScope =
      book.categoryId === filters.category ||
      (book.subcategoryId !== null &&
        qxParentOfSubcategory(book.subcategoryId) === filters.category) ||
      book.secondaryCategoryIds.includes(filters.category);
    if (!inScope) return false;
  }
  if (filters.subcategory !== 'all' && book.subcategoryId !== filters.subcategory) return false;
  if (filters.price !== 'all' && !matchesBand(book.priceMinorUnits, filters.price)) return false;
  const query = filters.query.trim().toLowerCase();
  if (query.length > 0) {
    const inTitle = book.title.toLowerCase().includes(query);
    const inAuthor = book.author?.toLowerCase().includes(query) ?? false;
    if (!inTitle && !inAuthor) return false;
  }
  return true;
}

export interface MarketplaceFacetCounts {
  readonly categories: Readonly<Record<MarketplaceCategoryId, number>>;
  readonly subcategories: Readonly<Record<string, number>>;
  readonly prices: Readonly<Record<MarketplacePriceBand, number>>;
  readonly categoryTotal: number;
  readonly priceTotal: number;
  readonly total: number;
}

interface MutableFacetCounts {
  categories: Record<string, number>;
  subcategories: Record<string, number>;
  prices: Record<MarketplacePriceBand, number>;
  total: number;
}

function emptyCounts(): MutableFacetCounts {
  return {
    categories: Object.fromEntries(QX_MAIN_CATEGORY_IDS.map((id) => [id, 0])),
    subcategories: {},
    prices: { free: 0, b1_10: 0, b11_30: 0, b31_50: 0, b51_plus: 0 },
    total: 0,
  };
}

function countInto(counts: MutableFacetCounts, book: MarketplaceBook): void {
  if (book.categoryId !== null) {
    counts.categories[book.categoryId] = (counts.categories[book.categoryId] ?? 0) + 1;
  }
  if (book.subcategoryId !== null) {
    counts.subcategories[book.subcategoryId] = (counts.subcategories[book.subcategoryId] ?? 0) + 1;
  }
  const band = priceBandFor(book.priceMinorUnits);
  if (band !== null) counts.prices[band] += 1;
  counts.total += 1;
}

export function computeFacetCounts(
  books: readonly MarketplaceBook[],
  filters: MarketplaceFilters,
): MarketplaceFacetCounts {
  const categoryCounts = emptyCounts();
  const subcategoryCounts = emptyCounts();
  const priceCounts = emptyCounts();
  const allCounts = emptyCounts();
  for (const book of books) {
    if (matchesFilters(book, filters)) {
      countInto(allCounts, book);
    }
    if (matchesFilters(book, { ...filters, category: 'all', subcategory: 'all' })) {
      countInto(categoryCounts, book);
    }
    if (
      filters.category !== 'all' &&
      matchesFilters(book, { ...filters, subcategory: 'all' }) &&
      (book.categoryId === filters.category || book.subcategoryId !== null)
    ) {
      countInto(subcategoryCounts, book);
    }
    if (matchesFilters(book, { ...filters, price: 'all' })) {
      countInto(priceCounts, book);
    }
  }
  return {
    categories: categoryCounts.categories as Readonly<Record<MarketplaceCategoryId, number>>,
    subcategories: subcategoryCounts.subcategories,
    prices: priceCounts.prices,
    categoryTotal: categoryCounts.total,
    priceTotal: priceCounts.total,
    total: allCounts.total,
  };
}

/** null (tiada data) sentiasa kalah — nilai tidak diketahui tak boleh menang isihan. */
function compareNullableDate(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b.localeCompare(a);
}

export function sortMarketplaceBooks(
  books: readonly MarketplaceBook[],
  sortId: MarketplaceSortId,
): readonly MarketplaceBook[] {
  const sorted = [...books];
  const byTitle = (a: MarketplaceBook, b: MarketplaceBook) => a.title.localeCompare(b.title);
  switch (sortId) {
    case 'newest':
      sorted.sort((a, b) => compareNullableDate(a.publishedAt, b.publishedAt) || byTitle(a, b));
      break;
    case 'price_low':
      sorted.sort(
        (a, b) =>
          (a.priceMinorUnits ?? Number.POSITIVE_INFINITY) -
            (b.priceMinorUnits ?? Number.POSITIVE_INFINITY) || byTitle(a, b),
      );
      break;
    case 'price_high':
      sorted.sort(
        (a, b) =>
          (b.priceMinorUnits ?? Number.NEGATIVE_INFINITY) -
            (a.priceMinorUnits ?? Number.NEGATIVE_INFINITY) || byTitle(a, b),
      );
      break;
    case 'title_a_z':
    default:
      sorted.sort(byTitle);
      break;
  }
  return sorted;
}

export interface MarketplacePage {
  readonly items: readonly MarketplaceBook[];
  readonly page: number;
  readonly pageCount: number;
  readonly total: number;
  readonly showingFrom: number;
  readonly showingTo: number;
}

export function paginateMarketplaceBooks(
  books: readonly MarketplaceBook[],
  page: number,
): MarketplacePage {
  const total = books.length;
  const pageCount = Math.max(1, Math.ceil(total / MARKETPLACE_PAGE_SIZE));
  const current = Math.min(Math.max(1, page), pageCount);
  const start = (current - 1) * MARKETPLACE_PAGE_SIZE;
  return {
    items: books.slice(start, start + MARKETPLACE_PAGE_SIZE),
    page: current,
    pageCount,
    total,
    showingFrom: total === 0 ? 0 : start + 1,
    showingTo: Math.min(start + MARKETPLACE_PAGE_SIZE, total),
  };
}
